import React, { useState } from 'react';
import { useIntl } from 'react-intl';
// eslint-disable-next-line xing/no-extraneous-import
import cx from 'classnames';

import { Dialog } from '@xingternal/dialog';
import { Button, TextButton } from '@xingternal/button';
import {
  EditButton,
  UpsellPopOver,
  useModuleContext,
  usePageContext,
} from '@xingternal/pages-common';
import { usePopOver } from '@xingternal/pop-over';

import { BottomSheet } from '@xingternal/bottom-sheet';
import { List, ListItem } from '@xingternal/list';

import {
  IconEdit,
  IconStar,
  IconStarFilled,
  IconShow,
  IconTrash,
  IconReaction,
  IconHide,
} from '@xingternal/icons';
import {
  getIDfromURN,
  trackPinAction,
  trackUnpinAction,
  trackHideAction,
  trackUnhideAction,
} from '@xingternal/pages-tracking';
import type * as Types from '@xingternal/pages-graphql-types';

import styles from './styles.css';

import {
  PUBLISHED,
  HIDDEN,
  SCHEDULED,
  DRAFT,
  CONTENT_FEED_TYPE_ARTICLE,
  CONTENT_FEED_TYPE_NEW_ARTICLE,
  CONTENT_FEED_TYPE_GENERIC,
  CONTENT_FEED_TYPE_COMPANY_ARTICLE,
} from '../constants';

import type { NewsProps } from '../News';

import { useDeletePostingMutation } from '../DeletePosting.gql-types';
import { useCompanyUpdatesDeleteMutation } from '../CompanyDeletePosting.gql-types';
import { usePinMutation } from '../PinActivity.gql-types';
import { useUnpinMutation } from '../UnpinActivity.gql-types';
import { useHidePostingMutation } from '../HidePosting.gql-types';
import { useUnhidePostingMutation } from '../UnhidePosting.gql-types';
import FEED_QUERY from '../FeedQuery.graphql';

type actionConfirmDialogType = {
  headline: string;
  text: string;
  cancel: string;
  confirm: string;
  action: () => void;
};

type NewsItemActionsProps = Pick<
  NewsProps,
  'feedQueryVars' | 'isDetailPage' | 'isFollowingPage'
> & {
  activityId: string | null;
  articleTypename: string;
  editPosting?: () => void;
  isPaidPage: boolean;
  isPinned: boolean;
  pageId: string;
  itemId?: string;
  publicationState?: Types.PostingPublicationState;
  selectedPosting: () => void;
  setIsItemLoading: (isLoading: boolean) => void;
  setLazyLoadingIsAllowed: (isAllowed: boolean) => void;
  xamUrl: any;
};

const NewsItemActions = ({
  activityId = null,
  articleTypename,
  editPosting,
  feedQueryVars,
  isPaidPage,
  isPinned,
  pageId,
  itemId,
  publicationState,
  selectedPosting,
  setIsItemLoading,
  setLazyLoadingIsAllowed,
  xamUrl,
}: NewsItemActionsProps) => {
  const [show, setShow] = useState<boolean>();
  const [
    actionConfirmDialog,
    setActionConfirmDialog,
  ] = useState<actionConfirmDialogType | null>();
  const popOver = usePopOver();
  const { pageContext } = usePageContext();
  const { moduleContext } = useModuleContext();
  const { focusType } = pageContext;
  const intl = useIntl();

  const feedQuery = {
    query: FEED_QUERY,
    variables: { ...feedQueryVars },
    fetchPolicy: 'network-only',
  };

  const removePostingFromCache = (cache: any) => {
    let { entityPageContentFeeds } = cache.readQuery(feedQuery);
    const newPostings = {
      collection: entityPageContentFeeds.collection.filter(
        (item: any) => item.object.id !== itemId
      ),
    };
    const newFeed = Object.assign({}, entityPageContentFeeds, newPostings);
    cache.writeQuery({
      ...feedQuery,
      data: {
        entityPageContentFeeds: newFeed,
      },
    });
  };

  const [deletePosting, { loading: deleteLoading }] = useDeletePostingMutation({
    optimisticResponse: {
      __typename: 'Mutation',
      postingsDeletePosting: {
        error: null,
        __typename: 'PostingsPostingDeleteResult',
      },
    },
    update: (cache) => {
      removePostingFromCache(cache);
    },
  });

  const [
    deleteCompanyPost,
    { loading: deleteCompanyPostLoading },
  ] = useCompanyUpdatesDeleteMutation({
    update: (cache) => {
      removePostingFromCache(cache);
    },
    onCompleted: () => {
      setLazyLoadingIsAllowed(true);
    },
  });

  const [pinActivity, { loading: pinLoading }] = usePinMutation({
    onCompleted: () => {
      setLazyLoadingIsAllowed(true);
      trackPinAction({ itemId, pageId, focusType });
    },
    refetchQueries: [FEED_QUERY],
  });

  // eslint-disable-next-line no-unused-vars
  const [unpinActivity, { loading: unpinLoading }] = useUnpinMutation({
    onCompleted: () => {
      setLazyLoadingIsAllowed(true);
      trackUnpinAction({ itemId, pageId, focusType });
    },
    refetchQueries: [FEED_QUERY],
  });

  const [hidePosting, { loading: hideLoading }] = useHidePostingMutation({
    update: (cache) => {
      removePostingFromCache(cache);
    },
    onCompleted: () => {
      setLazyLoadingIsAllowed(true);
      trackHideAction({ itemId, pageId, focusType });
    },
    refetchQueries: [FEED_QUERY],
  });

  const [unhidePosting, { loading: unhideLoading }] = useUnhidePostingMutation({
    update: (cache) => {
      setLazyLoadingIsAllowed(true);
      removePostingFromCache(cache);
    },
    onCompleted: () => {
      trackUnhideAction({ itemId, pageId, focusType });
    },
    refetchQueries: [FEED_QUERY],
  });

  const actionsDescriptionObj = {
    delete: {
      headline: 'NEWS_MODULE_DIALOG_DELETE_HEADLINE',
      text: 'NEWS_MODULE_DIALOG_DELETE_TEXT',
      cancel: 'NEWS_MODULE_DIALOG_DELETE_NO',
      confirm: 'NEWS_MODULE_DIALOG_DELETE_YES',
      action: () => {
        setIsItemLoading(true);
        if (articleTypename === CONTENT_FEED_TYPE_GENERIC) {
          itemId && deletePosting({ variables: { id: itemId } });
        }
        if (articleTypename === CONTENT_FEED_TYPE_COMPANY_ARTICLE) {
          const companyId = pageContext?.companyId;
          companyId &&
            itemId &&
            deleteCompanyPost({
              variables: { companyId, updateId: itemId },
            });
        }
      },
    },
    pin: {
      action: () => {
        setIsItemLoading(true);
        isPaidPage &&
          pinActivity({
            variables: {
              activityId: getIDfromURN(activityId),
              targetGlobalId: moduleContext.moduleGlobalId,
            },
          });
      },
    },
    unpin: {
      headline: 'NEWS_MODULE_DIALOG_UNPIN_HEADLINE',
      text: 'NEWS_MODULE_DIALOG_UNPIN_TEXT',
      cancel: 'NEWS_MODULE_DIALOG_UNPIN_NO',
      confirm: 'NEWS_MODULE_DIALOG_UNPIN_YES',
      action: () => {
        setIsItemLoading(true);
        isPaidPage &&
          unpinActivity({
            variables: {
              targetGlobalId: moduleContext.moduleGlobalId,
            },
          });
      },
    },
    hide: {
      headline: 'NEWS_MODULE_DIALOG_HIDE_HEADLINE',
      text: 'NEWS_MODULE_DIALOG_HIDE_TEXT',
      cancel: 'NEWS_MODULE_DIALOG_HIDE_NO',
      confirm: 'NEWS_MODULE_DIALOG_HIDE_YES',
      action: () => {
        setIsItemLoading(true);
        itemId && hidePosting({ variables: { id: itemId } });
      },
    },
    unhide: {
      headline: 'NEWS_MODULE_DIALOG_UNHIDE_HEADLINE',
      text: 'NEWS_MODULE_DIALOG_UNHIDE_TEXT',
      cancel: 'NEWS_MODULE_DIALOG_UNHIDE_NO',
      confirm: 'NEWS_MODULE_DIALOG_UNHIDE_YES',
      action: () => {
        setIsItemLoading(true);
        itemId && unhidePosting({ variables: { id: itemId } });
      },
    },
    edit: {
      action: () => {
        selectedPosting();
        editPosting && editPosting();
      },
    },
  };

  const isLoading =
    deleteLoading ||
    unpinLoading ||
    pinLoading ||
    unhideLoading ||
    hideLoading ||
    deleteCompanyPostLoading;

  const restrictions = {
    pin: !isPaidPage || !activityId || isLoading || publicationState === HIDDEN,
    hide:
      articleTypename !== CONTENT_FEED_TYPE_GENERIC ||
      hideLoading ||
      unhideLoading ||
      publicationState === DRAFT ||
      publicationState === SCHEDULED,
    delete:
      articleTypename === CONTENT_FEED_TYPE_ARTICLE ||
      articleTypename === CONTENT_FEED_TYPE_NEW_ARTICLE ||
      isLoading,
    edit: articleTypename !== CONTENT_FEED_TYPE_GENERIC || isLoading,
  };

  const handlePopOverAction = (action: () => void) => {
    if (restrictions.pin) action();
  };

  return (
    <>
      {/* Since useMediaListener is deprecated, we show/hide divs based on mediaQueries */}
      {/* Show on window  size > mobile */}
      <section
        className={cx(styles.actorActions, styles.desktopActions)}
        data-testid="POSTING_ACTIONS"
        id={`POSTING_${itemId}`}
      >
        {/* Edit */}
        <EditButton
          className={styles.editButton}
          size={'small'}
          icon={IconEdit}
          disabled={restrictions.edit}
          data-testid={'EDIT_POSTING'}
          onClick={() => {
            actionsDescriptionObj.edit.action();
          }}
        />
        {/* Pin/Unpin - Should be disabled in Scheduled posts tab */}
        {publicationState !== SCHEDULED && (
          <EditButton
            className={styles.editButton}
            size={'small'}
            icon={isPinned ? IconStarFilled : IconStar}
            ariaLabel={isPinned ? 'Star filled' : 'Star empty'}
            data-testid={'PIN_POSTING'}
            onClick={() => {
              if (isPinned) {
                setActionConfirmDialog(actionsDescriptionObj.unpin);
              } else {
                actionsDescriptionObj.pin.action();
              }
            }}
            innerRef={popOver.triggerRef}
            onMouseOver={() => handlePopOverAction(popOver.handleShow)}
            onMouseLeave={() => handlePopOverAction(popOver.handleHide)}
            disabled={restrictions.pin}
          />
        )}
        {/* Hide/Show */}
        <EditButton
          className={styles.editButton}
          size={'small'}
          data-testid={'HIDE_POSTING'}
          onClick={() => {
            if (publicationState === HIDDEN) {
              actionsDescriptionObj.unhide.action();
            } else {
              setActionConfirmDialog(actionsDescriptionObj.hide);
            }
          }}
          icon={publicationState === PUBLISHED ? IconHide : IconShow}
          ariaLabel={publicationState === PUBLISHED ? 'Hide' : 'Show'}
          disabled={restrictions.edit}
        />
        {/* Delete */}
        <EditButton
          className={styles.editButton}
          size={'small'}
          icon={IconTrash}
          ariaLabel={'Trash'}
          data-testid={'DELETE_POSTING'}
          onClick={() => {
            setActionConfirmDialog(actionsDescriptionObj.delete);
          }}
          disabled={restrictions.delete}
        />
        {/* Promote */}
        <EditButton
          iconAfter
          icon={IconReaction}
          ariaLabel={'Reaction'}
          data-testid={'PROMOTE_POSTING'}
          variant={'primary'}
          size={'small'}
          disabled={!xamUrl}
          href={xamUrl}
          target={'_blank'}
          className={cx(styles.editButton, styles.promote)}
        >
          <>{intl.formatMessage({ id: `NEWS_MODULE_PROMOTE_BUTTON` })}</>
        </EditButton>
        <UpsellPopOver moduleName="newsmodule" popOver={popOver} />
      </section>
      {/* Show on window size =< mobile, default */}
      <section
        className={cx(styles.actorActions, styles.mobileActions)}
        id={`POSTING_${itemId}`}
        data-testid="POSTING_ACTIONS"
      >
        <EditButton
          className={styles.editButton}
          data-testid={'NEWS_ITEM_ACTION_BUTTON'}
          size={'small'}
          icon={IconEdit}
          onClick={() => setShow(true)}
        />
        <EditButton
          className={styles.editButton}
          icon={IconReaction}
          ariaLabel="Reaction"
          data-testid={'PROMOTE_POSTING'}
          variant={'primary'}
          size={'small'}
          disabled={!xamUrl}
          href={xamUrl}
          target={'_blank'}
          skin={{ button: styles.promote }}
        />
      </section>
      {show && (
        <BottomSheet
          dimmerTitle="close"
          show={show}
          onOutsideClick={() => setShow(false)}
          data-testid={'NEWS_ITEM_ACTION_SHEET'}
        >
          <List>
            {/* Edit */}
            <ListItem>
              <Button
                size={'medium'}
                icon={IconEdit}
                disabled={restrictions.edit}
                data-testid={'EDIT_POSTING'}
                onClick={() => {
                  setShow(false);
                  actionsDescriptionObj.edit.action();
                }}
              >
                {intl.formatMessage({ id: 'NEWS_MODULE_EDIT_LABEL' })}
              </Button>
            </ListItem>
            {/* Pin/Unpin */}
            <ListItem>
              <TextButton
                size={'medium'}
                icon={isPinned ? IconStarFilled : IconStar}
                ariaLabel={isPinned ? 'Star filled' : 'Star empty'}
                data-testid={'PIN_POSTING'}
                innerRef={popOver.triggerRef}
                onClick={() => {
                  setShow(false);
                  if (isPinned) {
                    setActionConfirmDialog(actionsDescriptionObj.unpin);
                  } else {
                    actionsDescriptionObj.pin.action();
                  }
                }}
                onMouseOver={() => {
                  handlePopOverAction(popOver.handleShow);
                }}
                onMouseLeave={() => handlePopOverAction(popOver.handleHide)}
                disabled={restrictions.pin}
              >
                {intl.formatMessage({
                  id: isPinned
                    ? 'NEWS_MODULE_UNPIN_BUTTON'
                    : 'NEWS_MODULE_PIN_BUTTON',
                })}
              </TextButton>
            </ListItem>
            {/* Hide/Show */}
            <ListItem>
              <TextButton
                size={'medium'}
                icon={IconShow}
                data-testid={'HIDE_POSTING'}
                onClick={() => {
                  setShow(false);
                  if (publicationState === HIDDEN) {
                    actionsDescriptionObj.unhide.action();
                  } else {
                    setActionConfirmDialog(actionsDescriptionObj.hide);
                  }
                }}
                disabled={restrictions.hide}
              >
                {intl.formatMessage({
                  id:
                    publicationState === HIDDEN
                      ? 'NEWS_MODULE_SHOW_BUTTON'
                      : 'NEWS_MODULE_HIDE_BUTTON',
                })}
              </TextButton>
            </ListItem>
            {/* Delete */}
            <ListItem>
              <TextButton
                size={'medium'}
                icon={IconTrash}
                data-testid={'DELETE_POSTING'}
                disabled={restrictions.delete}
                onClick={() => {
                  setShow(false);
                  setActionConfirmDialog(actionsDescriptionObj.delete);
                }}
              >
                {intl.formatMessage({ id: 'NEWS_MODULE_DELETE_LABEL' })}
              </TextButton>
            </ListItem>
            <UpsellPopOver moduleName="newsmodule" popOver={popOver} />
          </List>
        </BottomSheet>
      )}
      <Dialog
        show={Boolean(actionConfirmDialog)}
        className={styles.newsmoduleDialog}
        testId={'POSTING_DIALOG'}
        isDestructive
        flavour="xdl"
        headline={
          actionConfirmDialog?.headline
            ? intl.formatMessage({ id: actionConfirmDialog?.headline })
            : ''
        }
        text={
          actionConfirmDialog?.text
            ? intl.formatMessage({ id: actionConfirmDialog?.text })
            : ''
        }
        cancelLabel={
          actionConfirmDialog?.cancel
            ? intl.formatMessage({ id: actionConfirmDialog?.cancel })
            : ''
        }
        confirmLabel={
          actionConfirmDialog?.confirm
            ? intl.formatMessage({
                id: actionConfirmDialog?.confirm,
              })
            : ''
        }
        onCancel={() => {
          setActionConfirmDialog(null);
        }}
        onConfirm={() => {
          actionConfirmDialog?.action();
          setActionConfirmDialog(null);
        }}
      />
    </>
  );
};

export default NewsItemActions;
